import { useState, useEffect } from "react";
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useSearchParams, useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import ApiProvider from "../../apiProvider/addToCartApi";
import { IMAGE_URL } from "../../network/apiClient";
import './myaccount.css';
import { useTranslation } from "../../context/TranslationContext";
import { useCurrency } from "../../context/CurrencyContext";

const CartPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { translateSync } = useTranslation();
    const { currency, getCurrencySymbol, getProductPrice, getProductMrp } = useCurrency();
    const isAuthenticated = useSelector((state) => state.auth.token);
    const [cartListDetails, setCartListDetails] = useState([]);
    const [totalPrice, setTotalPrice] = useState(0);
    const [cartId, setCartId] = useState(null);

    const getCartDetails = async () => {
        try {
            const token = localStorage.getItem('userToken');
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const userId = user._id || user.id;
            const guestId = localStorage.getItem('guestUserId');

            let result;
            if (token && userId) {
                result = await ApiProvider.getCart(userId, { type: 'user', userType: 'user' });
            } else if (guestId) {
                result = await ApiProvider.getCart(guestId, { type: 'guest', userType: 'guest' });
            } else {
                setCartListDetails([]);
                setTotalPrice(0);
                return;
            }

            if (result && result.status && result.response) {
                // Handle array response (take first cart)
                const responseData = result.response.data || result.response;
                const cartData = Array.isArray(responseData) ? responseData[0] : responseData;

                if (cartData && cartData.products) {
                    setCartId(cartData._id);
                    setTotalPrice(cartData.total || 0);

                    const mappedCart = cartData.products.map(item => {
                        // Fields are now directly on the item, NOT in item.productId

                        // Image Logic: Match the attribute row first
                        let imgSrc = null;
                        const cartAttributes = item.attributes || {};
                        const matchingRow = item.customerAttribute?.rowData?.find(row => {
                            return Object.entries(cartAttributes).every(([key, val]) => {
                                const rowKey = Object.keys(row).find(k => {
                                    const kLower = k.toLowerCase();
                                    const keyLower = key.toLowerCase();
                                    if (kLower === keyLower) return true;
                                    if ((kLower === 'color' || kLower === 'colour') && (keyLower === 'color' || keyLower === 'colour')) return true;
                                    return false;
                                });
                                return rowKey && String(row[rowKey]) === String(val);
                            });
                        });

                        // Price Logic: Prefer getProductPrice / getProductMrp
                        const sellingPrice = getProductPrice(item, matchingRow);
                        const mrp = getProductMrp(item, matchingRow);

                        // Stock Logic: Check if the matching row has stock
                        const isOutOfStock = !matchingRow || (matchingRow.stock <= 0);

                        const attrImg = matchingRow?.images?.[0] || item.customerAttribute?.rowData?.[0]?.images?.[0];

                        if (attrImg) {
                            imgSrc = `${attrImg.docPath}/${attrImg.docName}`;
                        } else if (item.productImage && item.productImage.length > 0) {
                            imgSrc = `${item.productImage[0].docPath}/${item.productImage[0].docName}`;
                        } else if (item.additionalImage && item.additionalImage.length > 0) {
                            imgSrc = `${item.additionalImage[0].docPath}/${item.additionalImage[0].docName}`;
                        }

                        // Dynamic Attribute Logic
                        let selectedAttributes = [];
                        if (item.attributeData && Array.isArray(item.attributeData)) {
                            selectedAttributes = item.attributeData.map(attr => {
                                // Handle value being array of objects or direct value
                                const val = (Array.isArray(attr.value) && attr.value.length > 0) ? attr.value[0].value : attr.value;
                                return {
                                    name: attr.name,
                                    value: val
                                };
                            }).filter(a => a.value);
                        } else if (item.attributes) {
                            // Fallback
                            selectedAttributes = Object.keys(item.attributes).map(key => ({
                                name: key,
                                value: item.attributes[key]
                            }));
                        }

                        return {
                            id: item.productCartId, // Use cart item ID for removal
                            originalProductId: item._id, // Real Product ID
                            cartItemId: item.productCartId,
                            productName: item.productName || "Unknown Product",
                            categoryName: item.categoryName || "",
                            price: mrp,
                            offerPrice: sellingPrice,
                            quantity: item.quantity,
                            categoryImage: imgSrc,
                            selectedAttributes: selectedAttributes,
                            attributes: item.attributes || {},
                            offerType: item.offerType,
                            isOutOfStock: isOutOfStock,
                            stock: matchingRow ? Number(matchingRow.stock) : (item.stock ? Number(item.stock) : 0)
                        };
                    });
                    // Calculate total based only on in-stock items with valid pricing
                    const calculatedTotal = mappedCart
                        .filter(item => !item.isOutOfStock && item.offerPrice !== null)
                        .reduce((sum, i) => sum + (i.offerPrice * i.quantity), 0);

                    setTotalPrice(calculatedTotal);
                    setCartListDetails(mappedCart);

                } else {
                    setCartListDetails([]);
                    setTotalPrice(0);
                }
            } else {
                // Clear cart if result status is false (e.g. "Cart is empty" error)
                setCartListDetails([]);
                setTotalPrice(0);
            }
        } catch (error) {
            console.error("Error fetching cart:", error);
            setCartListDetails([]);
            setTotalPrice(0);
        }
    };

    useEffect(() => {
        getCartDetails();
    }, [currency]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const Checkout = async () => {
        navigate("/checkout?type=addToCart");
    };

    const removeItem = async (id) => {
        if (!cartId) return;
        try {
            await ApiProvider.removeCartItem(cartId, id);
            // Refresh regardless of success status, because last item removal might return error "Cart is empty"
            getCartDetails();
        } catch (error) {
            console.error("Error removing item:", error);
            getCartDetails();
        }
    };

    const handleUpdateQty = async (item, newQty) => {
        if (newQty < 1) return;
        if (newQty > item.stock) {
            toast.error(`Only ${item.stock} items left in stock.`);
            return;
        }
        try {
            const token = localStorage.getItem('userToken');
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const userId = user._id || user.id;
            const guestId = localStorage.getItem('guestUserId');

            const payload = {
                type: token && userId ? 'user' : 'guest',
                userId: token && userId ? userId : undefined,
                guestUserId: !token ? (guestId || undefined) : undefined,
                userType: token && userId ? 'user' : 'guest',
                products: [
                    {
                        productId: item.originalProductId,
                        quantity: newQty,
                        price: item.offerPrice,
                        mrpPrice: item.price,
                        attributes: item.attributes || {}
                    }
                ]
            };

            const result = await ApiProvider.addToCart(payload);
            if (result && result.status) {
                getCartDetails();
            } else {
                alert("Failed to update quantity. Please try again.");
            }
        } catch (error) {
            console.error("Error updating quantity:", error);
        }
    };

    return (
        <>
            <Helmet>
                <title>Shopping Bag | Colours Boutique</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <div className="cart-page-wrapper" style={{
                fontFamily: "'Lato', sans-serif",
                backgroundColor: "#ebe7e1",
                backgroundImage: "url('https://www.transparenttextures.com/patterns/natural-paper.png')",
                color: "#3a3530",
                minHeight: "100vh",
                paddingTop: "100px"
            }}>

                {/* Minimal Header */}
                <section className="py-4 text-center">
                    <div className="container">
                        <h1 className="display-5 fw-bold text-uppercase" style={{ fontFamily: "'Playfair Display', serif", letterSpacing: "2px", color: "#3a3530" }}>
                            {translateSync('Shopping Bag')}
                        </h1>
                    </div>
                </section>

                {cartListDetails && cartListDetails.length === 0 ? (
                    <div className="text-center py-5">
                        <i className="fa fa-shopping-bag mb-4" style={{ fontSize: '4rem', color: '#eee' }}></i>
                        <h4 className="mt-3 text-uppercase" style={{ letterSpacing: "1px" }}>{translateSync('Your bag is empty')}</h4>
                        <p className="text-muted mb-4">{translateSync("Looks like you haven’t added any items yet.")}</p>
                        <button
                            className="btn btn-dark text-uppercase px-5 py-3 rounded-0"
                            onClick={() => navigate(`/saree`)}
                            style={{ letterSpacing: "2px", fontSize: "14px" }}
                        >
                            {translateSync('Browse Collection')}
                        </button>
                    </div>
                ) : (
                    <section className="pb-5 px-3 px-lg-5">
                        <div className="container-fluid">
                            <div className="row g-5">
                                {/* Cart Items List */}
                                <div className="col-lg-8">
                                    <div className="table-responsive">                                        <table className="table align-middle">
                                            <thead className="text-uppercase small text-muted" style={{ letterSpacing: "1px", borderBottom: "1px solid #eee" }}>
                                                <tr>
                                                    <th className="pb-3 fw-normal border-0 text-start" style={{ width: "50%" }}>{translateSync('Product')}</th>
                                                    <th className="pb-3 fw-normal border-0 text-center" style={{ width: "20%" }}>{translateSync('Price')}</th>
                                                    <th className="pb-3 fw-normal border-0 text-center" style={{ width: "15%" }}>{translateSync('Quantity')}</th>
                                                    <th className="pb-3 fw-normal border-0 text-end" style={{ width: "15%" }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {cartListDetails.map((ival) => (
                                                    <tr key={ival.id} style={{ borderBottom: "1px solid #eee", opacity: ival.isOutOfStock ? 0.5 : 1 }}>
                                                        <td className="py-4 border-0">
                                                            <div className="d-flex align-items-center">
                                                                <div style={{ width: "60px", height: "80px", overflow: "hidden", marginRight: "20px", flexShrink: 0 }}>
                                                                    <img
                                                                        src={(() => {
                                                                            // 0. Variant Image (Highest Priority for Color Selection)
                                                                            if (ival.variantImage) {
                                                                                const cleanPath = ival.variantImage.startsWith('/') ? ival.variantImage : '/' + ival.variantImage;
                                                                                return encodeURI(cleanPath);
                                                                            }
 
                                                                            // 1. Trust any path explicitly starting with /img (handles both Dhotis and Sarees)
                                                                            if (ival.categoryImage && (ival.categoryImage.trim().startsWith('/img') || ival.categoryImage.trim().startsWith('img'))) {
                                                                                const cleanPath = ival.categoryImage.startsWith('/') ? ival.categoryImage : '/' + ival.categoryImage;
                                                                                // Encode URI to handle spaces in filenames (e.g. "Cotton Dhoti.png")
                                                                                return encodeURI(cleanPath);
                                                                            }
 
                                                                            // 2. Legacy fallback for old product data that might just be "filename.png" or "products/filename.png"
                                                                            if (ival.categoryImage && (ival.categoryImage.startsWith("/products") || ival.categoryImage.includes("products"))) {
                                                                                const path = `/img${ival.categoryImage.startsWith('/') ? ival.categoryImage : '/' + ival.categoryImage}`.replace('//', '/');
                                                                                return encodeURI(path);
                                                                            }
 
                                                                            // 3. Last resort: external URL
                                                                            return ival.categoryImage ? IMAGE_URL + ival.categoryImage : '/img/logo/logo.png';
                                                                        })()}
                                                                        alt="Product"
                                                                        className="w-100 h-100"
                                                                        style={{ objectFit: "cover" }}
                                                                        onError={(e) => {
                                                                            console.log("Image load failed for:", e.target.src);
                                                                            // Fallback to logo if image missing
                                                                            if (!e.target.src.includes('logo.png')) {
                                                                                e.target.src = '/img/logo/logo.png';
                                                                            }
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <h6 className="mb-1 text-uppercase" style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.1rem" }}>
                                                                        {translateSync(ival.productName || ival.courseName)}
                                                                    </h6>
                                                                    {ival.isOutOfStock && (
                                                                        <span className="badge bg-danger text-uppercase mb-1" style={{ fontSize: '0.65rem' }}>Out of Stock</span>
                                                                    )}
                                                                    <p className="text-muted small mb-0 text-uppercase" style={{ fontSize: "0.8rem" }}>{translateSync(ival.categoryName)}</p>
                                                                    {ival.size && (
                                                                        <p className="text-muted small mb-0" style={{ fontSize: "0.8rem" }}>
                                                                            Size: <span>{ival.size}</span>
                                                                        </p>
                                                                    )}
                                                                    {ival.selectedAttributes && ival.selectedAttributes.map((attr, idx) => (
                                                                        <div key={idx} className="d-flex align-items-center mt-1">
                                                                            <span className="text-muted small me-2" style={{ fontSize: "0.8rem", textTransform: 'capitalize' }}>{translateSync(attr.name)}:</span>
                                                                            {(attr.name.toLowerCase() === 'colors' || attr.name.toLowerCase() === 'color') && typeof attr.value === 'string' && (attr.value.startsWith('#') || attr.value.startsWith('rgb')) ? (
                                                                                <div style={{
                                                                                    width: "15px",
                                                                                    height: "15px",
                                                                                    backgroundColor: attr.value,
                                                                                    borderRadius: "50%",
                                                                                    border: "1px solid #ddd"
                                                                                }} title={attr.value}></div>
                                                                            ) : (
                                                                                <span className="text-muted small" style={{ fontSize: "0.8rem" }}>{attr.value}</span>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="py-4 border-0 fw-bold text-center" style={{ fontSize: "1.1rem", textDecoration: ival.isOutOfStock ? 'line-through' : 'none', whiteSpace: "nowrap" }}>
                                                            {ival.offerPrice === null ? (
                                                                <span className="text-muted" style={{ fontSize: '0.9rem', fontStyle: 'italic', fontWeight: 'normal' }}>
                                                                    Currency not available
                                                                </span>
                                                            ) : (
                                                                `${getCurrencySymbol()} ${Number(ival.offerPrice || ival.price).toLocaleString()}`
                                                            )}
                                                        </td>
                                                        <td className="py-4 border-0 text-center">
                                                            <div className="d-inline-flex align-items-center border border-dark rounded-3" style={{ height: "35px", backgroundColor: "#fff" }}>
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-link text-dark px-2 py-0 text-decoration-none fw-bold" 
                                                                    onClick={() => handleUpdateQty(ival, ival.quantity - 1)}
                                                                    disabled={ival.quantity <= 1}
                                                                >
                                                                    -
                                                                </button>
                                                                <span className="px-2 fw-bold" style={{ minWidth: "25px" }}>{ival.quantity}</span>
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-link text-dark px-2 py-0 text-decoration-none fw-bold" 
                                                                    onClick={() => handleUpdateQty(ival, ival.quantity + 1)}
                                                                >
                                                                    +
                                                                </button>
                                                            </div>
                                                        </td>
                                                        <td className="py-4 border-0 text-end">
                                                            <button
                                                                className="btn btn-link text-dark p-0 text-decoration-none"
                                                                onClick={() => removeItem(ival.id)}
                                                                style={{ fontSize: "0.9rem", textTransform: "uppercase", letterSpacing: "1px" }}
                                                            >
                                                                Remove
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Order Summary */}
                                {(() => {
                                    const hasUnavailableCurrency = cartListDetails.some(item => item.offerPrice === null);
                                    return (
                                        <div className="col-lg-4">
                                            <div className="p-4 shadow-sm" style={{ borderRadius: "0", backgroundColor: "#fff", border: "1px solid #ddd" }}>
                                                <h5 className="text-uppercase mb-4 fw-bold" style={{ letterSpacing: "1px", fontFamily: "'Playfair Display', serif" }}>Order Summary</h5>

                                                <div className="d-flex justify-content-between mb-3">
                                                    <span className="text-muted">Subtotal</span>
                                                    <span className="fw-bold">{getCurrencySymbol()} {totalPrice.toLocaleString()}</span>
                                                </div>
                                                <div className="d-flex justify-content-between mb-3">
                                                    <span className="text-muted">Shipping</span>
                                                    <span className="text-success">Free</span>
                                                </div>

                                                <hr className="my-4" />

                                                <div className="d-flex justify-content-between mb-4">
                                                    <span className="fw-bold text-uppercase">Total</span>
                                                    <span className="fw-bold fs-4">{getCurrencySymbol()} {totalPrice.toLocaleString()}</span>
                                                </div>

                                                <button
                                                    className="btn btn-dark w-100 py-3 text-uppercase rounded-0"
                                                    onClick={() => (isAuthenticated ? Checkout() : navigate("/login"))}
                                                    disabled={(cartListDetails.length > 0 && cartListDetails.every(item => item.isOutOfStock)) || hasUnavailableCurrency}
                                                    style={{
                                                        letterSpacing: "2px",
                                                        fontWeight: "600",
                                                        backgroundColor: hasUnavailableCurrency ? "#ccc" : undefined,
                                                        border: hasUnavailableCurrency ? "1px solid #ccc" : undefined,
                                                        cursor: hasUnavailableCurrency ? "not-allowed" : "pointer"
                                                    }}
                                                >
                                                    {hasUnavailableCurrency
                                                        ? translateSync('Unavailable items in cart')
                                                        : (cartListDetails.length > 0 && cartListDetails.every(item => item.isOutOfStock)
                                                            ? translateSync('All items out of stock')
                                                            : translateSync('Checkout'))}
                                                </button>

                                                {hasUnavailableCurrency && (
                                                    <div className="alert alert-warning mt-3 rounded-0 py-2 small" role="alert" style={{ fontSize: '0.8rem' }}>
                                                        Some items are not available in {currency === 'SGD' ? 'Singapore Dollars (SGD)' : currency}. Please remove them or change currency to proceed.
                                                    </div>
                                                )}

                                                <div className="mt-3 text-center">
                                                    <small className="text-muted">Secure Checkout - SSL Encrypted</small>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    </section>
                )}
            </div>
        </>
    );
};

export default CartPage;